import React from "react";
import { StyleSheet, View, ScrollView, Platform, type StyleProp, type ViewStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../../ThemedText";
import { ThemedButton } from "../../ThemedButton";
import { ThemedToolTip } from "../../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";
import type { EditorBlockType, IEditorFormatState } from "../types";

export interface IEditToolbarProps {
  visible?: boolean;
  position?: { top: number; left: number };
  activeFormats?: IEditorFormatState;
  formats?: IEditorFormatState;
  blockType?: EditorBlockType;
  activeBlockType?: EditorBlockType;
  isFormatActive?: (key: keyof IEditorFormatState, value?: string) => boolean;
  onToggleFormat?: (formatKey: keyof IEditorFormatState) => void;
  onSetBlockType?: (type: EditorBlockType) => void;
  onSetFontFamily?: (font?: string) => void;
  onSetFontSize?: (size?: string) => void;
  onInsertLink?: () => void;
  onInsertTable?: () => void;
  onInsertContact?: () => void;
  onInsertGroup?: () => void;
  onInsertMedia?: () => void;
  onInsertMemory?: () => void;
  onClearFormatting?: () => void;
  onRemoveFormatting?: () => void;
  onClose?: () => void;
  isStandalonePreview?: boolean;
  style?: StyleProp<ViewStyle>;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  isFormatPainterActive?: boolean;
  onToggleFormatPainter?: () => void;
}

/**
 * EditToolbar (formerly TextFormatToolbar)
 *
 * Floating popup toolbar for rich text typography and block formatting.
 * Features:
 * - Generous width with smooth horizontal scrolling so no buttons are truncated.
 * - Standardized entirely with ThemedButton across all actions.
 * - Prevents mousedown default on web to preserve active selection.
 * - Strict adherence to Emerald/Blue/Rose palette (Strict Color Ban).
 */
export const EditToolbar: React.FC<IEditToolbarProps> = ({
  visible = true,
  position,
  activeFormats,
  formats,
  blockType,
  activeBlockType,
  isFormatActive: customIsFormatActive,
  onToggleFormat,
  onSetBlockType,
  onSetFontFamily,
  onSetFontSize,
  onInsertLink,
  onInsertTable,
  onInsertContact,
  onInsertGroup,
  onInsertMedia,
  onInsertMemory,
  onClearFormatting,
  onRemoveFormatting,
  onClose,
  isStandalonePreview = false,
  style,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  isFormatPainterActive,
  onToggleFormatPainter,
}) => {
  const currentFormats = activeFormats || formats || {};
  const currentBlockType = blockType || activeBlockType || "paragraph";

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const tintColor = useThemeColor({}, "tint");
  const isDark = cardBg === "#131520";

  if (!visible) return null;

  const isFormatActive = (key: keyof IEditorFormatState, value?: string) => {
    if (customIsFormatActive) {
      return customIsFormatActive(key, value);
    }
    if (key === "fontFamily") return (currentFormats.fontFamily || "sans") === (value || "sans");
    if (key === "fontSize") return (currentFormats.fontSize || "md") === (value || "md");
    return !!currentFormats[key];
  };

  const containerStyle = isStandalonePreview
    ? [
        styles.popupContainer,
        {
          backgroundColor: cardBg,
          borderColor,
          position: "relative" as const,
          top: 0,
          left: 0,
        },
        style,
      ]
    : [
        styles.popupContainer,
        {
          backgroundColor: cardBg,
          borderColor,
          position: "absolute" as const,
          zIndex: 99999,
          elevation: 16,
          top: position ? Math.max(8, position.top) : 8,
          left: position ? Math.max(8, position.left) : 12,
        },
        style,
      ];

  return (
    <View
      style={containerStyle}
      testID="edit-toolbar"
      {...(Platform.OS === "web"
        ? ({
            onMouseDown: (e: any) => e.preventDefault(),
          } as any)
        : {})}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="always"
        {...(Platform.OS === "web"
          ? ({
              onMouseDown: (e: any) => e.preventDefault(),
            } as any)
          : {})}
      >
        {/* Undo, Redo & Format Painter */}
        {(onUndo || onRedo || onToggleFormatPainter) && (
          <>
            <View style={styles.section}>
              {onUndo && (
                <ThemedToolTip text="Undo (Ctrl+Z)">
                  <ThemedButton
                    title=""
                    icon={<Ionicons name="arrow-undo-outline" size={14} color={canUndo !== false ? textColor : "#9CA3AF"} />}
                    variant="minimal"
                    disabled={canUndo === false}
                    style={styles.toolBtn}
                    onPress={onUndo}
                    accessibilityLabel="Undo (Ctrl+Z)"
                  />
                </ThemedToolTip>
              )}

              {onRedo && (
                <ThemedToolTip text="Redo (Ctrl+Y)">
                  <ThemedButton
                    title=""
                    icon={<Ionicons name="arrow-redo-outline" size={14} color={canRedo !== false ? textColor : "#9CA3AF"} />}
                    variant="minimal"
                    disabled={canRedo === false}
                    style={styles.toolBtn}
                    onPress={onRedo}
                    accessibilityLabel="Redo (Ctrl+Y)"
                  />
                </ThemedToolTip>
              )}

              {onToggleFormatPainter && (
                <ThemedToolTip text={isFormatPainterActive ? "Format Painter (Armed - click to apply)" : "Format Painter"}>
                  <ThemedButton
                    title=""
                    icon={<Ionicons name="brush-outline" size={14} color={isFormatPainterActive ? "#fff" : textColor} />}
                    variant={isFormatPainterActive ? "primary" : "minimal"}
                    style={styles.toolBtn}
                    onPress={onToggleFormatPainter}
                    accessibilityLabel="Format Painter"
                  />
                </ThemedToolTip>
              )}
            </View>
            <View style={[styles.divider, { backgroundColor: borderColor }]} />
          </>
        )}

        {/* Block Types */}
        <View style={styles.section}>
          <ThemedToolTip text="Normal paragraph text">
            <ThemedButton
              title="P"
              variant={currentBlockType === "paragraph" ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={styles.btnText}
              onPress={() => onSetBlockType?.("paragraph")}
              accessibilityLabel="Normal paragraph"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Heading 1">
            <ThemedButton
              title="H1"
              variant={currentBlockType === "h1" ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { fontWeight: "800" }]}
              onPress={() => onSetBlockType?.("h1")}
              accessibilityLabel="Heading 1"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Heading 2">
            <ThemedButton
              title="H2"
              variant={currentBlockType === "h2" ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { fontWeight: "700" }]}
              onPress={() => onSetBlockType?.("h2")}
              accessibilityLabel="Heading 2"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Heading 3">
            <ThemedButton
              title="H3"
              variant={currentBlockType === "h3" ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { fontWeight: "700" }]}
              onPress={() => onSetBlockType?.("h3")}
              accessibilityLabel="Heading 3"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Blockquote">
            <ThemedButton
              title="“"
              variant={currentBlockType === "quote" ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { fontSize: 15 }]}
              onPress={() => onSetBlockType?.("quote")}
              accessibilityLabel="Blockquote"
            />
          </ThemedToolTip>
        </View>

        <View style={[styles.divider, { backgroundColor: borderColor }]} />

        {/* Inline Formatting (B, I, U, S) */}
        <View style={styles.section}>
          <ThemedToolTip text="Bold (Ctrl+B)">
            <ThemedButton
              title="B"
              variant={isFormatActive("bold") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { fontWeight: "900" }]}
              onPress={() => onToggleFormat?.("bold")}
              accessibilityLabel="Bold (Ctrl+B)"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Italic (Ctrl+I)">
            <ThemedButton
              title="I"
              variant={isFormatActive("italic") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { fontStyle: "italic" }]}
              onPress={() => onToggleFormat?.("italic")}
              accessibilityLabel="Italic (Ctrl+I)"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Underline (Ctrl+U)">
            <ThemedButton
              title="U"
              variant={isFormatActive("underline") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { textDecorationLine: "underline" }]}
              onPress={() => onToggleFormat?.("underline")}
              accessibilityLabel="Underline (Ctrl+U)"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Strikethrough">
            <ThemedButton
              title="S"
              variant={isFormatActive("strike") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { textDecorationLine: "line-through" }]}
              onPress={() => onToggleFormat?.("strike")}
              accessibilityLabel="Strikethrough"
            />
          </ThemedToolTip>
        </View>

        <View style={[styles.divider, { backgroundColor: borderColor }]} />

        {/* Font Family */}
        <View style={styles.section}>
          <ThemedToolTip text="Sans Font">
            <ThemedButton
              title="Sans"
              variant={isFormatActive("fontFamily", "sans") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={styles.btnText}
              onPress={() => onSetFontFamily?.("sans")}
              accessibilityLabel="Sans Font"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Serif Font">
            <ThemedButton
              title="Serif"
              variant={isFormatActive("fontFamily", "serif") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { fontFamily: Platform.OS === "ios" ? "Georgia" : "serif" }]}
              onPress={() => onSetFontFamily?.("serif")}
              accessibilityLabel="Serif Font"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Mono Font">
            <ThemedButton
              title="Mono"
              variant={isFormatActive("fontFamily", "mono") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={[styles.btnText, { fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace" }]}
              onPress={() => onSetFontFamily?.("mono")}
              accessibilityLabel="Mono Font"
            />
          </ThemedToolTip>
        </View>

        <View style={[styles.divider, { backgroundColor: borderColor }]} />

        {/* Font Size */}
        <View style={styles.section}>
          <ThemedToolTip text="Small Text (12px)">
            <ThemedButton
              title="Sm"
              variant={isFormatActive("fontSize", "sm") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={styles.btnText}
              onPress={() => onSetFontSize?.("sm")}
              accessibilityLabel="Small Size"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Normal Text (14px)">
            <ThemedButton
              title="Md"
              variant={isFormatActive("fontSize", "md") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={styles.btnText}
              onPress={() => onSetFontSize?.("md")}
              accessibilityLabel="Normal Size"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Large Text (18px)">
            <ThemedButton
              title="Lg"
              variant={isFormatActive("fontSize", "lg") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={styles.btnText}
              onPress={() => onSetFontSize?.("lg")}
              accessibilityLabel="Large Size"
            />
          </ThemedToolTip>

          <ThemedToolTip text="Huge Text (22px)">
            <ThemedButton
              title="Xl"
              variant={isFormatActive("fontSize", "xl") ? "primary" : "minimal"}
              style={styles.toolBtn}
              textStyle={styles.btnText}
              onPress={() => onSetFontSize?.("xl")}
              accessibilityLabel="Huge Size"
            />
          </ThemedToolTip>
        </View>

        <View style={[styles.divider, { backgroundColor: borderColor }]} />

        {/* Insert Triggers */}
        <View style={styles.section}>
          {onInsertLink && (
            <ThemedToolTip text="Insert Link (Ctrl+K)">
              <ThemedButton
                title=""
                icon={<Ionicons name="link-outline" size={14} color={textColor} />}
                variant="minimal"
                style={styles.toolBtn}
                onPress={onInsertLink}
                accessibilityLabel="Insert Link"
              />
            </ThemedToolTip>
          )}

          {onInsertTable && (
            <ThemedToolTip text="Insert Table">
              <ThemedButton
                title=""
                icon={<Ionicons name="grid-outline" size={14} color={textColor} />}
                variant="minimal"
                style={styles.toolBtn}
                onPress={onInsertTable}
                accessibilityLabel="Insert Table"
              />
            </ThemedToolTip>
          )}

          {onInsertContact && (
            <ThemedToolTip text="Mention Contact (@)">
              <ThemedButton
                title=""
                icon={<Ionicons name="person-outline" size={14} color="#059669" />}
                variant="minimal"
                style={[styles.toolBtn, { borderColor: "rgba(5, 150, 105, 0.3)" }]}
                onPress={onInsertContact}
                accessibilityLabel="Mention Contact"
              />
            </ThemedToolTip>
          )}

          {onInsertGroup && (
            <ThemedToolTip text="Mention Group (@)">
              <ThemedButton
                title=""
                icon={<Ionicons name="people-outline" size={14} color="#2563EB" />}
                variant="minimal"
                style={[styles.toolBtn, { borderColor: "rgba(37, 99, 235, 0.3)" }]}
                onPress={onInsertGroup}
                accessibilityLabel="Mention Group"
              />
            </ThemedToolTip>
          )}

          {onInsertMedia && (
            <ThemedToolTip text="Insert Media (+)">
              <ThemedButton
                title=""
                icon={<Ionicons name="image-outline" size={14} color="#D97706" />}
                variant="minimal"
                style={[styles.toolBtn, { borderColor: "rgba(217, 119, 6, 0.3)" }]}
                onPress={onInsertMedia}
                accessibilityLabel="Insert Media"
              />
            </ThemedToolTip>
          )}

          {onInsertMemory && (
            <ThemedToolTip text="Mention Memory (#)">
              <ThemedButton
                title=""
                icon={<Ionicons name="sparkles-outline" size={14} color="#E11D48" />}
                variant="minimal"
                style={[styles.toolBtn, { borderColor: "rgba(225, 29, 72, 0.3)" }]}
                onPress={onInsertMemory}
                accessibilityLabel="Mention Memory"
              />
            </ThemedToolTip>
          )}
        </View>

        {(onRemoveFormatting || onClearFormatting) && (
          <>
            <View style={[styles.divider, { backgroundColor: borderColor }]} />
            <ThemedToolTip text="Remove Formatting">
              <ThemedButton
                title=""
                icon={<Ionicons name="remove-circle-outline" size={14} color="#EF4444" />}
                variant="minimal"
                style={[styles.toolBtn, { backgroundColor: "rgba(239, 68, 68, 0.08)", borderColor: "#EF4444" }]}
                onPress={onRemoveFormatting || onClearFormatting}
                accessibilityLabel="Remove formatting"
              />
            </ThemedToolTip>
          </>
        )}

        {onClose && (
          <ThemedButton
            title=""
            icon={<Ionicons name="close" size={14} color={textColor} />}
            variant="minimal"
            style={styles.toolBtn}
            onPress={onClose}
            accessibilityLabel="Close toolbar"
          />
        )}
      </ScrollView>
    </View>
  );
};

// Aliased export for backwards compatibility
export const TextFormatToolbar = EditToolbar;

const styles = StyleSheet.create({
  popupContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 5,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 16,
    width: "auto",
    maxWidth: "96%",
    alignSelf: "flex-start",
    ...(Platform.OS === "web" ? ({ width: "fit-content" } as any) : {}),
  },
  scrollView: {
    flexGrow: 0,
    maxWidth: "100%",
  },
  scrollContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingRight: 8,
  },
  section: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  divider: {
    width: 1,
    height: 18,
    marginHorizontal: 3,
  },
  toolBtn: {
    minWidth: 26,
    height: 26,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
  },
  btnText: {
    fontSize: 11,
    lineHeight: 13,
  },
});
